/**
 * ModeCardRow — selectable card row for choosing one of N options where
 * the choice is consequential enough to deserve more visual weight than
 * a `<select>` dropdown (DEV-816).
 *
 * Used in MissionKpiWizard for the `color_mode` choice (Progress / Deadline /
 * Pace). Each row pairs:
 *   - A label (the mode name)
 *   - A help string (one-line "use this when...")
 *   - An optional sparkline SVG that hints at how the mode behaves
 *
 * Click anywhere on the row to select. Selected state uses the accent color
 * border + a tinted background. Unselected rows have a subtle border.
 *
 * Designed as a controlled component — the parent owns the selected value
 * and re-renders with it. No internal state.
 */

import React from 'react';
import styles from './ModeCardRow.module.css';

export interface ModeOption<T extends string = string> {
  /** The value this option represents (e.g. 'score'). */
  value: T;
  /** User-facing label (e.g. 'Progress (% toward target)'). */
  label: string;
  /** One-line explanation shown below the label. */
  help: string;
  /**
   * Optional sparkline rendered as a small SVG. The sparkline gives the
   * user a visual hint of what the mode does — e.g. a rising line for
   * progress, a 3-band bar for deadline, dots-on-a-trend-line for pace.
   * Pass any ReactNode (typically an inline <svg>).
   */
  sparkline?: React.ReactNode;
  /**
   * When true, the card is greyed out and not clickable. Use for options
   * that require sibling fields to be populated first — e.g. Pace mode
   * needs both tracking_start_date and deadline_date. Pair with
   * `disabledReason` to explain what the user needs to do to unlock it.
   * (DEV-816 / Skip)
   */
  disabled?: boolean;
  /** Shown inside the disabled card to explain how to enable it. */
  disabledReason?: string;
}

export interface ModeCardListProps<T extends string = string> {
  /** Options in the order they should appear. */
  options: ModeOption<T>[];
  /** The currently-selected value. */
  value: T | undefined;
  /** Called when the user picks a different option. */
  onChange: (value: T) => void;
  /** Optional aria-label for the radio group. */
  ariaLabel?: string;
  /** Optional className for the wrapping container. */
  className?: string;
}

/**
 * Render a vertically-stacked list of mode cards. Behaves like a radio
 * group — exactly one option is selected at a time.
 */
export function ModeCardList<T extends string = string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
}: ModeCardListProps<T>) {
  return (
    <div
      className={[styles.list, className].filter(Boolean).join(' ')}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map((opt) => (
        <ModeCardRow
          key={opt.value}
          option={opt}
          selected={opt.value === value}
          onSelect={() => onChange(opt.value)}
        />
      ))}
    </div>
  );
}

interface ModeCardRowProps<T extends string = string> {
  option: ModeOption<T>;
  selected: boolean;
  onSelect: () => void;
}

/**
 * A single card row. Exposed for callers that need to compose card rows
 * outside the standard list (rare). Most callers should use ModeCardList.
 */
export function ModeCardRow<T extends string = string>({
  option,
  selected,
  onSelect,
}: ModeCardRowProps<T>) {
  const isDisabled = !!option.disabled;

  // Keyboard support: Space/Enter selects. Roving tabindex managed by the
  // browser via the radio role on the wrapping list. Disabled cards
  // ignore keyboard activation.
  const onKey = (e: React.KeyboardEvent) => {
    if (isDisabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      className={`${styles.row} ${selected ? styles.selected : ''} ${isDisabled ? styles.disabled : ''}`}
      role="radio"
      aria-checked={selected}
      aria-disabled={isDisabled || undefined}
      tabIndex={isDisabled ? -1 : (selected ? 0 : -1)}
      onClick={() => { if (!isDisabled) onSelect(); }}
      onKeyDown={onKey}
      title={isDisabled ? option.disabledReason : undefined}
    >
      <div className={styles.meta}>
        <div className={styles.label}>{option.label}</div>
        <div className={styles.help}>{option.help}</div>
        {isDisabled && option.disabledReason && (
          <div className={styles.disabledReason}>
            {/* Lock-style icon for "unavailable until prereq met" */}
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
              strokeLinejoin="round" aria-hidden="true" style={{ marginInlineEnd: 4, verticalAlign: '-1px' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            {option.disabledReason}
          </div>
        )}
      </div>
      {option.sparkline && (
        <div className={styles.spark} aria-hidden="true">
          {option.sparkline}
        </div>
      )}
    </div>
  );
}
